/* LogsControlRenderer.js */

"use strict";

const _ = require("lodash");
const LogHighlightRules = require("./mode/LogHighlightRules");
const LOG_SEQUENCE = require("./LogSequence").LOG_SEQUENCE;
const dateFormat = require("dateformat");
const EDITOR_LINE_HEIGHT = 15;
const LOG_LEVEL_REGEX = /(?<space>^\s*)(?<notation>\.|\(\*\)|(?:>>|<<)|__|!\?|\?\?|\d+})(?: )/;
const SearchControlComponent = require("./search/SearchControlComponent");
const ComboBoxControl = require("@gluenetworks/combobox").Single;
const CheckboxControl = require("@gluenetworks/input_checkbox_control");
const Util = require("./util/Util");
const DateUtil = require("./util/DateUtil");
const CommonFilterIcon = require("../icons/rawicons/filter.icon.svg.js");
const ScrollLockIcon = require("../icons/rawicons/scroll.lock.svg.js");

class LogsControlRenderer {
    constructor(model) {
        this._model = model;
        this._uniqueTagList = this._getUniqueTagList(this._model.logs);

        this._rootElement = document.createElement("div");
        this._rootElement.classList.add("logs-control");
        this._editorDiv = document.createElement("div");
        const ace = require("ace-builds/src-min-noconflict/ace");

        this._editor = ace.edit(this._editorDiv);
        this._editorSession = this._editor.getSession();
        this._searchControl = new SearchControlComponent(
            {
                searchString: this._model.searchString,
                searchCaseSensitive: this._model.searchCaseSensitive,
                scrollLock: this._model.scrollLock,
                buildActionBar: this._model.buildActionBar,
            },
            this._editor,
            this._editorSession
        );

        this._logFiltersChangedBind = this._onLogTagFiltersChanged.bind(this);
        this._logLevelFilterChangedBind = this._onLogLevelFiltersChanged.bind(this);
        this._onMouseClickHideFilterModalBoxBind = this._onMouseClickHideFilterModalBox.bind(this);
        this._onMouseMoveDebounce = _.debounce(this._onMouseMove.bind(this), 500);
        this._logLevelCheckboxes = [];
        this._tagFilterCheckboxes = [];
        this._gutterTagFilterCheckboxes = [];
        this._buildTagsDetailsBox();
        this._buildActionBar();

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("newLogs", (this._onNewLogs = this._onNewLogs.bind(this)));
        this._model.on("logLevels", (this._onLogLevels = this._onLogLevels.bind(this)));
        this._model.on("tagFilters", (this._onTagFilters = this._onTagFilters.bind(this)));
        this._model.on("logSequence", (this._onLogSequence = this._onLogSequence.bind(this)));
        this._model.on("appendLogs", (this._onAppendLogs = this._onAppendLogs.bind(this)));
        this._model.on("searchString", (this._onSearchString = this._onSearchString.bind(this)));
        this._model.on(
            "searchCaseSensitive",
            (this._onSearchCaseSensitive = this._onSearchCaseSensitive.bind(this))
        );
        this._model.on(
            "actionBarVisible",
            (this._onActionBarVisible = this._onActionBarVisible.bind(this))
        );
        this._model.on("controlHeight", (this._onControlHeight = this._onControlHeight.bind(this)));
        this._model.on("scrollLock", (this._onScrollLock = this._onScrollLock.bind(this)));
        this._model.on(
            "highlightPreviousSearchStringMatch",
            (this._onHighlightPreviousSearchStringMatch =
                this._onHighlightPreviousSearchStringMatch.bind(this))
        );
        this._model.on(
            "highlightNextSearchStringMatch",
            (this._onHighlightNextSearchStringMatch =
                this._onHighlightNextSearchStringMatch.bind(this))
        );
        this._tagsButton.addEventListener(
            "click",
            (this._onTagsDetailsButtonClick = this._onTagsDetailsButtonClick.bind(this))
        );
        this._connectionInfoButton.addEventListener("click", this._onTagsDetailsButtonClick);

        this._registerActionBarListeners();
    }

    _registerActionBarListeners() {
        if (this._model.buildActionBar) {
            this._logSequenceComboBox.on(
                "UserValue",
                (this._onLogSequenceButtonClick = this._onLogSequenceButtonClick.bind(this))
            );
            this._logSequenceComboBox.on("UserFocus", this._onMouseClickHideFilterModalBoxBind);
            this._filterButton.addEventListener(
                "click",
                (this._onFilterButtonClick = this._onFilterButtonClick.bind(this))
            );
            this._searchControl.on(
                "UserSearchStringChanged",
                (this._onUserSearchStringChanged = this._onUserSearchStringChanged.bind(this))
            );
            this._searchControl.on(
                "UserSearchCaseSensitiveChanged",
                (this._onUserSearchCaseSensitiveChanged =
                    this._onUserSearchCaseSensitiveChanged.bind(this))
            );
            this._searchControl.on("scrollToBottom", this._onScrollLock);
            this._scrollLock.addEventListener(
                "click",
                (this._onScrollLockClick = this._onScrollLockClick.bind(this))
            );
        } else {
            this._searchControl.on(
                "UserSearchResultsCountChanged",
                (this._onUserSearchResultsCountChanged =
                    this._onUserSearchResultsCountChanged.bind(this))
            );
            this._searchControl.on(
                "CurrentSearchStringSequenceNumberChanged",
                (this._onCurrentSearchStringSequenceNumberChanged =
                    this._onCurrentSearchStringSequenceNumberChanged.bind(this))
            );
        }
    }

    _unregisterListeners() {
        this._model.removeListener("newLogs", this._onNewLogs);
        this._model.removeListener("logLevels", this._onLogLevels);
        this._model.removeListener("tagFilters", this._onTagFilters);
        this._model.removeListener("logSequence", this._onLogSequence);
        this._model.removeListener("appendLogs", this._onAppendLogs);
        this._model.removeListener("searchString", this._onSearchString);
        this._model.removeListener("searchCaseSensitive", this._onSearchCaseSensitive);
        this._model.removeListener("actionBarVisible", this._onActionBarVisible);
        this._model.removeListener("controlHeight", this._onControlHeight);
        this._model.removeListener("scrollLock", this._onScrollLock);
        this._tagsButton.removeEventListener("click", this._onTagsDetailsButtonClick);
        this._connectionInfoButton.removeEventListener("click", this._onTagsDetailsButtonClick);

        this._unregisterActionBarListeners();
        this._removeCheckBoxes(
            this._gutterTagFilterCheckboxes,
            this._tagsDetailsDiv,
            this._logFiltersChangedBind
        );
        if (this._editor) {
            this._editor.removeEventListener("guttermousemove", this._onGutterMouseMove);
            this._editor.removeEventListener("mousemove", this._onMouseMoveDebounce);
            if (this._onMouseClickHideFilterModalBoxBind) {
                document.removeEventListener("click", this._onMouseClickHideFilterModalBoxBind);
            }
            document.removeEventListener("click", this._onMouseClickHideTagsDetailsBox);
        }
    }

    _unregisterActionBarListeners() {
        if (this._model.buildActionBar) {
            this._logSequenceComboBox.removeListener("UserValue", this._onLogSequenceButtonClick);
            this._logSequenceComboBox.removeListener(
                "UserFocus",
                this._onMouseClickHideFilterModalBoxBind
            );
            this._filterButton.removeEventListener("click", this._onFilterButtonClick);
            this._searchControl.removeListener(
                "UserSearchStringChanged",
                this._onUserSearchStringChanged
            );
            this._searchControl.removeListener(
                "UserSearchCaseSensitiveChanged",
                this._onUserSearchCaseSensitiveChanged
            );
            this._searchControl.removeListener("scrollToBottom", this._onScrollLock);
            this._scrollLock.removeEventListener("click", this._onScrollLockClick);
            this._removeCheckBoxes(
                this._logLevelCheckboxes,
                this._logLevelDetailsContents,
                this._logLevelFilterChangedBind
            );
            this._removeCheckBoxes(
                this._tagFilterCheckboxes,
                this._filterTagsDetailsContents,
                this._logFiltersChangedBind
            );
        } else {
            this._searchControl.removeListener(
                "UserSearchResultsCountChanged",
                this._onUserSearchResultsCountChanged
            );
            this._searchControl.removeListener(
                "CurrentSearchStringSequenceNumberChanged",
                this._onCurrentSearchStringSequenceNumberChanged
            );
        }
    }

    _buildTagsDetailsBox() {
        this._tagsDetailsBox = document.createElement("div");
        this._tagsDetailsBox.classList.add("tags-tooltip");
        let leftArrow = document.createElement("div");
        leftArrow.classList.add("arrow-left");

        let tagsDetailsTooltipContents = document.createElement("div");
        tagsDetailsTooltipContents.classList.add("tags-tooltip-details");
        this._tagsDetailsHeader = document.createElement("div");
        this._tagsDetailsHeader.classList.add("tags_header");

        this._tagsButton = document.createElement("button");
        this._tagsButton.value = "tags";
        this._tagsButton.textContent = "Tags";
        this._tagsButton.style.display = "none";
        this._connectionInfoButton = document.createElement("button");
        this._connectionInfoButton.value = "connectionInfo";
        this._connectionInfoButton.textContent = "Connection Info";
        this._connectionInfoButton.style.display = "none";
        this._tagsDetailsDiv = document.createElement("div");
        this._tagsDetailsDiv.classList.add("tags_contents");
        this._connectionInfoDiv = document.createElement("div");
        this._connectionInfoDiv.classList.add("connection_info");

        this._tagsDetailsHeader.appendChild(this._tagsButton);
        this._tagsDetailsHeader.appendChild(this._connectionInfoButton);
        tagsDetailsTooltipContents.appendChild(this._tagsDetailsHeader);
        tagsDetailsTooltipContents.appendChild(this._tagsDetailsDiv);
        tagsDetailsTooltipContents.appendChild(this._connectionInfoDiv);
        this._tagsDetailsBox.appendChild(leftArrow);
        this._tagsDetailsBox.appendChild(tagsDetailsTooltipContents);
    }

    _buildActionBar() {
        if (!this._model.buildActionBar) {
            return;
        }
        this._actionDiv = document.createElement("div");
        this._actionDiv.classList.add("actions");

        let logSequenceOptions = [
            {label: LOG_SEQUENCE.NONE.displayName, value: LOG_SEQUENCE.NONE.value},
            {
                label: LOG_SEQUENCE.LOG_EVENT_NUMBER.displayName,
                value: LOG_SEQUENCE.LOG_EVENT_NUMBER.value,
            },
            {label: LOG_SEQUENCE.LINE_NUMBER.displayName, value: LOG_SEQUENCE.LINE_NUMBER.value},
            {label: LOG_SEQUENCE.TIME_STAMP.displayName, value: LOG_SEQUENCE.TIME_STAMP.value},
            {label: LOG_SEQUENCE.TIME_PASSED.displayName, value: LOG_SEQUENCE.TIME_PASSED.value},
            {
                label: LOG_SEQUENCE.EVENT_DURATION.displayName,
                value: LOG_SEQUENCE.EVENT_DURATION.value,
            },
        ];
        this._logSequenceComboBox = new ComboBoxControl({
            options: logSequenceOptions,
            allowClear: false,
            placeholder: "Log Sequence",
            showFilter: false,
            dropUp: true,
        });
        this._logSequenceComboBox.renderer.rootElement.classList.add("log_sequence");

        this._filterButton = document.createElement("button");
        this._filterButton.innerHTML = CommonFilterIcon.icon;
        this._filterButton.classList.add("filterBtn");
        this._filterButton.title = "Filter Logs";

        this._scrollLock = document.createElement("button");
        this._scrollLock.innerHTML = ScrollLockIcon.icon;
        this._scrollLock.classList.add("scroll-lock");
        this._scrollLock.title = "Scroll Lock Off";

        this._buildLogFilters();

        let filterSpan = document.createElement("span");
        filterSpan.classList.add("filter-root");
        filterSpan.appendChild(this._filterContainer);
        filterSpan.appendChild(this._filterButton);

        this._actionDiv.appendChild(this._searchControl.renderer.rootElement);
        this._actionDiv.appendChild(this._scrollLock);
        this._actionDiv.appendChild(filterSpan);
        this._actionDiv.appendChild(this._logSequenceComboBox.renderer.rootElement);
    }

    _buildLogFilters() {
        this._filterContainer = document.createElement("div");
        this._filterContainer.classList.add("filter-container");
        let filterDiv = document.createElement("div");
        filterDiv.classList.add("filter");
        let arrowDiv = document.createElement("div");
        arrowDiv.classList.add("arrow-down");
        let logLevelDetailsHeader = document.createElement("div");
        logLevelDetailsHeader.classList.add("loglevels_header");
        logLevelDetailsHeader.innerText = "Log Levels";
        this._logLevelDetailsContents = document.createElement("div");
        this._logLevelDetailsContents.classList.add("loglevels_contents");
        let logLevelKeys = Object.keys(this._model.logLevels);
        for (let index = 0; index < logLevelKeys.length; index++) {
            let logLevelKey = logLevelKeys[index];
            let checkBox = this._createCheckBox(
                _.startCase(_.lowerCase(logLevelKey)),
                logLevelKey,
                this._model.logLevels[logLevelKey],
                this._logLevelFilterChangedBind
            );
            this._logLevelDetailsContents.appendChild(checkBox.renderer.rootElement);
            this._logLevelCheckboxes.push(checkBox);
        }

        let filterTagsDetailsHeader = document.createElement("div");
        filterTagsDetailsHeader.classList.add("tags_header");
        filterTagsDetailsHeader.innerHTML = "Tags";
        this._filterTagsDetailsContents = document.createElement("div");
        this._filterTagsDetailsContents.classList.add("tags_contents");

        this._tagFilterCheckboxes = this._buildTagsCheckBox(
            this._uniqueTagList,
            this._filterTagsDetailsContents
        );

        filterDiv.appendChild(logLevelDetailsHeader);
        filterDiv.appendChild(this._logLevelDetailsContents);
        filterDiv.appendChild(filterTagsDetailsHeader);
        filterDiv.appendChild(this._filterTagsDetailsContents);
        this._filterContainer.appendChild(filterDiv);
        this._filterContainer.appendChild(arrowDiv);
    }

    _getFilteredLogs(logs, logLevels, whiteListedTags) {
        let logsByLineNumber = [];
        for (let index = 0; index < logs.length; index++) {
            if (this._isLogVisible(logs[index], logLevels, whiteListedTags)) {
                logsByLineNumber.push(logs[index]);
            }
        }
        return logsByLineNumber;
    }

    _isLogVisible(currentLog, logLevels, whiteListedTags) {
        let visibleLog = _.get(logLevels, [currentLog.lvl], true);
        let tags = currentLog.tags ? currentLog.tags : [];
        if (visibleLog && !_.isEmpty(tags) && whiteListedTags) {
            for (let index = 0; index < tags.length && visibleLog; index++) {
                visibleLog = whiteListedTags.find((element) => element.name === tags[index]).active;
            }
        }
        return visibleLog;
    }

    _setLogsOnEditor() {
        // render new logs on editor
        this._logsByLineNumber = this._getFilteredLogs(
            this._model.logs,
            this._model.logLevels,
            this._uniqueTagList
        );
        this._clearGutterDecorations();
        this._editor.setValue("");
        this._updateLogSequence();
        this._updateLogLevelCheckboxes();

        this._editorSession.insert({row: 0, column: 0}, "");
        this._addLogsOnEditor(this._logsByLineNumber);
    }

    _appendLogsOnEditor(newLogs) {
        let filteredLogs = this._getFilteredLogs(
            newLogs,
            this._model.logLevels,
            this._uniqueTagList
        );
        this._logsByLineNumber = this._logsByLineNumber.concat(filteredLogs);
        this._updateLogSequence();
        this._addLogsOnEditor(filteredLogs);
        this._recalculateFolds();
    }

    _addLogsOnEditor(logs) {
        let row = this._editorSession.getLength() - 1;
        let multipleLines = "";
        for (let index = 0; index < logs.length; index++, row++) {
            let currentLog = logs[index];
            let msg = currentLog.msg ? currentLog.msg : "";
            multipleLines = multipleLines + msg + "\n";
            if (index % 1000 === 0) {
                this._editorSession.insert(
                    {
                        row: this._editorSession.getLength(),
                        column: 0,
                    },
                    multipleLines
                );
                multipleLines = "";
            }
            if (!_.isEmpty(currentLog.tags) || currentLog.connectionInfo) {
                this._editorSession.addGutterDecoration(row, "log_tag");
            }
        }
        if (multipleLines) {
            this._editorSession.insert(
                {
                    row: this._editorSession.getLength(),
                    column: 0,
                },
                multipleLines
            );
        }
        if (!this._model.scrollLock) {
            this.scrollTop();
        }
        if (!this._model.buildActionBar) {
            return;
        }
        if (this._logsFiltered()) {
            this._filterButton.innerHTML = CommonFilterIcon.toggledIcon;
            this._filterButton.classList.remove("no_filter");
        } else {
            this._filterButton.innerHTML = CommonFilterIcon.icon;
            this._filterButton.classList.add("no_filter");
        }
    }

    _clearGutterDecorations() {
        let rowsCount = this._editorSession.getLength();
        for (let i = 0; i < rowsCount; i++) {
            if (this._editorSession.$decorations[i] !== "") {
                this._editorSession.removeGutterDecoration(i, "log_tag");
            }
        }
    }

    _buildTagsCheckBox(tags, parentElement) {
        let tagsCheckboxes = [];
        if (!_.isEmpty(tags)) {
            let tagsList = tags;
            for (let index = 0; index < tagsList.length; index++) {
                let currentTag = tagsList[index];
                let currentTagIndex = index;
                if (_.isString(currentTag)) {
                    currentTagIndex = this._uniqueTagList.findIndex(
                        (element) => element.name === currentTag
                    );
                    currentTag = this._uniqueTagList[currentTagIndex];
                }
                let tagName = currentTag.name.substring(0, 50);
                let checkBoxElement = this._createCheckBox(
                    tagName,
                    currentTagIndex,
                    currentTag.active,
                    this._logFiltersChangedBind
                );
                tagsCheckboxes.push(checkBoxElement);
                parentElement.appendChild(checkBoxElement.renderer.rootElement);
            }
        } else {
            parentElement.innerText = "No tags to show.";
        }
        return tagsCheckboxes;
    }

    _removeCheckBoxes(checkboxes, parentElement, clickListener) {
        if (_.isEmpty(checkboxes) || _.isEmpty(parentElement)) {
            return;
        }
        checkboxes.forEach((checkbox) => {
            if (_.isFunction(clickListener)) {
                checkbox.removeListener("UserValue", clickListener);
            }
            parentElement.removeChild(checkbox.renderer.rootElement);
            checkbox.shutdown();
        });
        checkboxes.splice(0, checkboxes.length);
        while (parentElement.firstChild) {
            parentElement.firstChild.remove();
        }
    }

    _createCheckBox(name, id, checked, clickListener) {
        let checkboxNew = new CheckboxControl({
            value: checked,
            label: name,
            labelShown: true,
            borderless: true,
        });
        checkboxNew.renderer.rootElement.setAttribute("data-id", id);
        checkboxNew.on("UserValue", clickListener);
        return checkboxNew;
    }

    _initializeEditor() {
        this._editor.setBehavioursEnabled(false);
        this._editor.setShowPrintMargin(false);
        this._editor.container.style.lineHeight = EDITOR_LINE_HEIGHT / 10;
        this._editor.renderer.setScrollMargin(0, 10, 0, 10);

        let basePath = "/resources/ace-builds/src-min-noconflict";
        if (window.location.pathname !== "/" && window.location.pathname !== "/index.html") {
            basePath = window.location.pathname + basePath;
        }
        ace.config.set("basePath", basePath);

        this._editor.on(
            "guttermousemove",
            (this._onGutterMouseMove = _.debounce(this._onGutterMouseMove.bind(this), 100))
        );
        this._editor.on("mousemove", this._onMouseMoveDebounce);
        this._editor.setReadOnly(true);
        this._editor.renderer.setDisplayIndentGuides(false);
        this._setModeOnEditor();
        this._editor.commands.removeCommand("find");
    }

    _setModeOnEditor() {
        let TextMode = ace.require("ace/mode/text").Mode;
        let mode = new TextMode();
        const GluLogFoldMode = require("./mode/LogFoldingRules").GluLogFoldMode;
        let foldingRules = new GluLogFoldMode();
        mode.foldingRules = foldingRules;
        this._editorSession.setMode(mode);
        this._applyHighlightRulesOnEditor(LogHighlightRules.getLogHighlightRules());
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._editor.destroy();
        this._editor = undefined;
        this._editorSession = undefined;
        this._logsByLineNumber = undefined;
        this._logFiltersChangedBind = undefined;
        this._logLevelFilterChangedBind = undefined;
        this._onMouseClickHideFilterModalBoxBind = undefined;
        this._onMouseMoveDebounce = undefined;
        this._textSelectionMarker = undefined;
        this._tagDetailsDisplayedForRow = undefined;
        this._logLevelDetailsContents = undefined;
        this._messageDetailsTooltip = undefined;
        this._tagFilterCheckboxes = [];
        this._gutterTagFilterCheckboxes = [];
        this._logLevelCheckboxes = [];

        return this._searchControl.shutdown().then(() => {
            this._searchControl = undefined;
            this._editorDiv.remove();
            this._tagsDetailsBox.remove();
            this._rootElement.remove();
            this._rootElement = undefined;
        });
    }

    render() {
        this._rootElement.appendChild(this._editorDiv);
        this._rootElement.appendChild(this._tagsDetailsBox);
        if (this._model.buildActionBar) {
            this._rootElement.appendChild(this._actionDiv);
        }
        if (Util.isMacOS()) {
            this._rootElement.classList.add("mac");
        }
        let controlHeight = this._getControlHeightAndEditorLines(this._model.controlHeight);
        this._editorDiv.style.height = controlHeight.inPixels + "px";
        if (this._model.buildActionBar) {
            this._filterContainer.style["max-height"] = controlHeight.inPixels + "px";
            this._logSequenceComboBox.setValue(this._model.logSequence);
        }
        //create editor & session
        this._initializeEditor();
        this._setLogsOnEditor();
        setTimeout(() => {
            this._setScrollLock();
        });
        this._setActionBarVisible(this._model.actionBarVisible);
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElement;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    getSearchStringMatchCount() {
        return this._searchControl.renderer.getSearchStringMatchCount();
    }

    getHighlightedSearchStringMatchSequenceNumber() {
        return this._searchControl.renderer.getCurrentMatchSequenceNumber();
    }

    /**
     * scrolls to last row of editor
     */
    scrollTop() {
        this._editor.scrollToRow(this._editorSession.getLength() - 1);
    }
    /*******************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when requested to highlight previous match in the model
     */
    _onHighlightPreviousSearchStringMatch() {
        this._searchControl.highlightPreviousSearchStringMatch();
    }

    /**
     * Fired when requested to highlight next match in the model
     */
    _onHighlightNextSearchStringMatch() {
        this._searchControl.highlightNextSearchStringMatch();
    }

    /**
     * Fired when the logs have been updated in the model
     * @private
     */
    _onNewLogs() {
        //re-calculate unique tag list & re render tags list
        this._uniqueTagList = this._getUniqueTagList(this._model.logs);
        this._setLogsOnEditor();
        if (this._model.buildActionBar) {
            this._removeCheckBoxes(
                this._tagFilterCheckboxes,
                this._filterTagsDetailsContents,
                this._logFiltersChangedBind
            );
            this._tagFilterCheckboxes = this._buildTagsCheckBox(
                this._uniqueTagList,
                this._filterTagsDetailsContents
            );
        }
    }

    /**
     * Fired when log levels are updated in the model
     * @private
     */
    _onLogLevels() {
        this._setLogsOnEditor();
    }

    _onTagFilters(newTagFilters) {
        this._updateTagFilters(newTagFilters);
    }

    _onAppendLogs(newLogs) {
        this._uniqueTagList = _.unionBy(
            this._uniqueTagList,
            this._getUniqueTagList(newLogs),
            "name"
        );
        this._uniqueTagList = _.sortBy(this._uniqueTagList, "name");
        this._appendLogsOnEditor(newLogs);
        if (this._model.buildActionBar) {
            this._removeCheckBoxes(
                this._tagFilterCheckboxes,
                this._filterTagsDetailsContents,
                this._logFiltersChangedBind
            );
            this._tagFilterCheckboxes = this._buildTagsCheckBox(
                this._uniqueTagList,
                this._filterTagsDetailsContents
            );
        }
    }

    _recalculateFolds() {
        let collapsedFolds = this._editorSession.$foldData;
        collapsedFolds.forEach((currentFold) => {
            this._editorSession.unfold(currentFold.range, false);
            let newFoldRange = this._editorSession.getFoldWidgetRange(currentFold.range.start.row);
            this._editorSession.foldAll(currentFold.range.start.row, newFoldRange.end.row, 0);
        });
    }

    /**
     * Fired when the search string has been updated in the model
     * @private
     */
    _onSearchString() {
        this._searchControl.setSearchString(this._model.searchString);
    }

    _onSearchCaseSensitive() {
        this._searchControl.setSearchCaseSensitive(this._model.searchCaseSensitive);
    }

    /**
     * Fired when action bar visibility is updated in the model
     * @private
     */
    _onActionBarVisible() {
        this._setActionBarVisible(this._model.isActionBarVisible());
    }

    /**
     * Fired when the log sequence has been updated in the model
     * @private
     */
    _onLogSequence() {
        this._setLogSequence(this._model.logSequence); // update UI button to indicate current log sequence in effect, update logs renderer
    }

    /**
     * Fired when setControlHeight is invoked on the component with new height
     * @private
     */
    _onControlHeight() {
        this._setControlHeight(this._model.controlHeight);
    }

    /**
     * Fired when scrollLock is updated in the model
     * @private
     */
    _onScrollLock() {
        this._setScrollLock();
    }

    _onTagsDetailsButtonClick(e) {
        if (e.currentTarget.value === "tags" && !this._tagsButton.classList.contains("active")) {
            this._tagsButton.classList.add("active");
            this._connectionInfoButton.classList.remove("active");
            this._tagsDetailsDiv.style.display = "block";
            this._connectionInfoDiv.style.display = "none";
        } else if (
            e.currentTarget.value === "connectionInfo" &&
            !this._connectionInfoButton.classList.contains("active")
        ) {
            this._connectionInfoButton.classList.add("active");
            this._tagsButton.classList.remove("active");
            this._connectionInfoDiv.style.display = "block";
            this._tagsDetailsDiv.style.display = "none";
        }
    }

    /**
     * Fired when the logs sequence button been clicked on the UI
     * @private
     */
    _onLogSequenceButtonClick() {
        this._model.logSequence = this._logSequenceComboBox.value;
        this._model._triggerLogSequenceChangedEvent();
    }

    /**
     * Fired when the tag filters are updated on UI
     * @param event triggered
     * @private
     */
    _onLogTagFiltersChanged(newVal, prevVal, e) {
        let tagIndex = e.currentTarget.getAttribute("data-id");
        if (tagIndex === undefined || tagIndex === null) {
            tagIndex = e.currentTarget.parentElement.getAttribute("data-id");
        }
        let tagObj = this._uniqueTagList[tagIndex];
        tagObj.active = newVal;
        if (this._model.buildActionBar && !this._filterContainer.contains(e.target)) {
            let checkBoxInFilterModelBox = this._tagFilterCheckboxes[tagIndex];
            checkBoxInFilterModelBox.setValue(newVal);
            this._hideTagsDetailsBox();
        }
        this._setLogsOnEditor();
    }

    _updateTagFilters(newTagFilters) {
        let tagsUpdated = 0;
        _.forEach(newTagFilters, (newTagDetails) => {
            let uniqueTagDetailsIndex = _.findIndex(this._uniqueTagList, [
                "name",
                newTagDetails.name,
            ]);
            let uniqueTagDetails =
                uniqueTagDetailsIndex >= 0 ? this._uniqueTagList[uniqueTagDetailsIndex] : undefined;
            if (uniqueTagDetails && uniqueTagDetails.active !== newTagDetails.active) {
                tagsUpdated++;
                uniqueTagDetails.active = newTagDetails.active;
                let checkBoxInFilterModelBox = this._tagFilterCheckboxes[uniqueTagDetailsIndex];
                checkBoxInFilterModelBox.setValue(uniqueTagDetails.active);
                //update UI filter box
            }
        });
        if (tagsUpdated > 0) {
            this._setLogsOnEditor();
        }
    }

    _updateLogLevelCheckboxes() {
        let logLevelCheckBoxes = this._logLevelCheckboxes;
        for (let i = 0; i < logLevelCheckBoxes.length; i++) {
            let checkBox = logLevelCheckBoxes[i];
            checkBox.value =
                this._model.logLevels[checkBox.renderer.rootElement.getAttribute("data-id")];
        }
    }

    /**
     * Fired when the log level filters are updated on UI
     * @param event triggered
     * @private
     */
    _onLogLevelFiltersChanged(newVal, prevVal, e) {
        let logLevel = e.currentTarget.getAttribute("data-id");
        if (!logLevel) {
            logLevel = e.currentTarget.parentElement.getAttribute("data-id");
        }
        this._model._logLevels[logLevel] = newVal;
        this._setLogsOnEditor();
        this._model._triggerLogFiltersChangedEvent();
    }

    /**
     * Fired when the filter button is clicked on UI
     * @private
     */
    _onFilterButtonClick() {
        this._toggleFilterModalBox();
    }

    /**
     * Fired when mouse moves on Editor Gutter
     * @param event triggered
     * @private
     */
    _onGutterMouseMove(e) {
        //hide message details tooltip
        if (this._messageDetailsTooltip) {
            this._messageDetailsTooltip.hide();
        }
        const gutterRegion = e.editor.renderer.$gutterLayer.getRegion(e);
        const tagsDetailsVisible = this._tagsDetailsBox.style.display !== "none";
        const domEvent = _.get(e, "domEvent");
        const domEventPath = _.isFunction(_.get(domEvent, "composedPath"))
            ? domEvent.composedPath()
            : _.get(domEvent, "path", []);
        const pathFirstElem = _.first(domEventPath);
        const isLogTagMarker = _.get(pathFirstElem, "classList")?.contains("log_tag") === true;
        if (gutterRegion === "markers" && isLogTagMarker) {
            //show hover over window with checkboxes of tags
            let currentRow = e.getDocumentPosition().row;
            if (
                !this._tagDetailsDisplayedForRow ||
                this._tagDetailsDisplayedForRow !== currentRow
            ) {
                this._tagDetailsDisplayedForRow = currentRow;
                let currentLog = this._logsByLineNumber[this._tagDetailsDisplayedForRow];
                let currentLogHasTags = !_.isEmpty(currentLog.tags);
                if (currentLogHasTags) {
                    this._tagsButton.style.display = "inline";
                    this._tagsButton.classList.add("active");
                    this._tagsDetailsDiv.style.display = "block";
                    this._removeCheckBoxes(
                        this._gutterTagFilterCheckboxes,
                        this._tagsDetailsDiv,
                        this._logFiltersChangedBind
                    );
                    this._gutterTagFilterCheckboxes = this._buildTagsCheckBox(
                        currentLog.tags,
                        this._tagsDetailsDiv
                    );
                } else {
                    this._tagsButton.style.display = "none";
                    this._tagsDetailsDiv.style.display = "none";
                }
                if (currentLog.connectionInfo) {
                    this._connectionInfoButton.style.display = "inline";
                    if (currentLogHasTags) {
                        this._connectionInfoButton.classList.remove("active");
                        this._connectionInfoDiv.style.display = "none";
                    } else {
                        this._connectionInfoButton.classList.add("active");
                        this._connectionInfoDiv.style.display = "block";
                    }
                    this._connectionInfoDiv.innerText = JSON.stringify(
                        currentLog.connectionInfo,
                        null,
                        4
                    );
                } else {
                    this._connectionInfoButton.style.display = "none";
                    this._connectionInfoDiv.style.display = "none";
                }

                this._rootElement.appendChild(this._tagsDetailsBox);
                this._tagsDetailsBox.style.left =
                    e.domEvent.target.getBoundingClientRect().left + 10 + "px";
                this._tagsDetailsBox.style.top =
                    e.domEvent.target.getBoundingClientRect().top - 4 + "px";
                if (this._tagsDetailsBox.style.display === "none") {
                    document.addEventListener(
                        "click",
                        (this._onMouseClickHideTagsDetailsBox =
                            this._onMouseClickHideTagsDetailsBox.bind(this))
                    );
                    this._tagsDetailsBox.style.display = "flex";
                }
            }
        } else if (tagsDetailsVisible) {
            this._hideTagsDetailsBox();
        }
    }

    _onMouseMove(e) {
        this._showLogMessageDetails(e);
    }

    /**
     * Fired when mouse moves on Editor
     * @param event triggered
     * @private
     */
    _onMouseClickHideTagsDetailsBox(e) {
        if (!this._tagsDetailsBox.contains(e.target)) {
            this._hideTagsDetailsBox();
        }
    }

    _onMouseClickHideFilterModalBox(e) {
        if (!this._filterContainer.contains(e.target) && !this._filterButton.contains(e.target)) {
            this._hideFilterModalBox();
        }
    }

    _onUserSearchStringChanged(searchString) {
        this._model.setSearchString(searchString, true);
    }

    _onUserSearchCaseSensitiveChanged(searchCaseSensitive) {
        this._model.setSearchCaseSensitive(searchCaseSensitive);
    }

    _onUserSearchResultsCountChanged(newUserSearchResultsCount, oldUserSearchResultsCount) {
        this._model._triggerUserSearchResultsCountChanged(
            newUserSearchResultsCount,
            oldUserSearchResultsCount
        );
    }

    _onCurrentSearchStringSequenceNumberChanged(
        newCurrentHighlightedSearchStringNumberChanged,
        oldCurrentHighlightedSearchStringNumberChanged
    ) {
        this._model._triggerCurrentSearchStringSequenceNumberChanged(
            newCurrentHighlightedSearchStringNumberChanged,
            oldCurrentHighlightedSearchStringNumberChanged
        );
    }

    _onScrollLockClick() {
        this._model.scrollLock = !this._model.scrollLock;
        this._model._triggerScrollLockChangedEvent();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _setActionBarVisible(visible) {
        if (this._model.buildActionBar) {
            this._actionDiv.style.display = visible ? "block" : "none";
            this._setControlHeight(this._model.controlHeight);
        }
    }

    _setLogSequence(logSequence) {
        if (this._model.buildActionBar && this._logSequenceComboBox.value !== logSequence) {
            this._logSequenceComboBox.setValue(logSequence);
        }
        this._updateLogSequence();
    }

    _setControlHeight(newHeight) {
        let controlHeight = this._getControlHeightAndEditorLines(newHeight);
        this._editorDiv.style.height = controlHeight.inPixels + "px";
        if (this._model.buildActionBar) {
            this._filterContainer.style["max-height"] = controlHeight.inPixels + "px";
        }
        this._editor.resize();
    }

    _setScrollLock() {
        if (this._model.buildActionBar) {
            if (this._model.scrollLock) {
                this._scrollLock.classList.add("enabled");
                this._scrollLock.title = "Scroll Lock On";
            } else {
                this._scrollLock.classList.remove("enabled");
                this._scrollLock.title = "Scroll Lock Off";
                this.scrollTop();
            }
        }
        this._searchControl.setScrollLock(this._model.scrollLock);
    }

    /**
     * Show/hide filter modal box
     * @private
     */
    _toggleFilterModalBox() {
        let filterSpan = this._rootElement.querySelector(".filter-root");
        if (this._isFilterModalBoxVisible()) {
            this._filterContainer.classList.remove("visible");
            document.removeEventListener("click", this._onMouseClickHideFilterModalBoxBind);
            filterSpan.removeChild(this._filterContainer);
        } else {
            filterSpan.prepend(this._filterContainer);
            this._filterContainer.classList.add("visible");
            document.addEventListener("click", this._onMouseClickHideFilterModalBoxBind);
        }
    }

    _isFilterModalBoxVisible() {
        return this._filterContainer.classList.contains("visible");
    }

    _hideFilterModalBox() {
        if (this._isFilterModalBoxVisible()) {
            this._toggleFilterModalBox();
        }
    }

    /**
     * Hides Tags details hover over box & detaches it from DOM
     * @private
     */
    _hideTagsDetailsBox() {
        if (this._tagsDetailsBox.style.display !== "none") {
            this._tagsDetailsBox.style.display = "none";
            this._rootElement.removeChild(this._tagsDetailsBox);
            document.removeEventListener("click", this._onMouseClickHideTagsDetailsBox);
            this._tagDetailsDisplayedForRow = undefined;
        }
    }

    /**
     * Applies given highlight rules on the editor
     * @param rules - text highight rules object
     * @private
     */
    _applyHighlightRulesOnEditor(rules) {
        let mode = this._editorSession.$mode;
        let Tokenizer = ace.require("ace/tokenizer").Tokenizer;
        let tokenizer = new Tokenizer(rules);
        mode.$tokenizer = tokenizer;
        this._editorSession.bgTokenizer.setTokenizer(mode.getTokenizer());
    }

    _getUniqueTagList(logs) {
        let uniqueTagList = [];
        let uniqueTags = [];
        for (let index = 0; index < logs.length; index++) {
            let currentLogTags = logs[index].tags;
            if (!_.isEmpty(currentLogTags)) {
                for (let tagIndex = 0; tagIndex < currentLogTags.length; tagIndex++) {
                    let currentTag = currentLogTags[tagIndex];
                    if (uniqueTags.indexOf(currentTag) === -1) {
                        uniqueTags.push(currentTag);
                        uniqueTagList.push({name: currentTag, active: true});
                    }
                }
            }
        }
        return _.sortBy(uniqueTagList, "name");
    }

    _logsFiltered() {
        return this._model.logs.length !== this._logsByLineNumber.length;
    }

    _getControlHeightAndEditorLines(newHeight) {
        let controlHeight = {};
        let heightInt = parseFloat(newHeight);
        if (!isNaN(heightInt)) {
            heightInt =
                heightInt - (this._isActionBarVisible() ? this._actionDiv.clientHeight + 10 : 0);
            controlHeight.inPixels = heightInt;
        }
        return controlHeight;
    }

    _updateLogSequence() {
        this._editorSession.gutterRenderer = {
            logs: this._logsByLineNumber,
            maxLlnLength: this._model._maxLlnLength,
            logSequence: this._model.logSequence,
            startTime: _.get(this._model.logs, "0.time", undefined),
            getWidth: function (session, lastLineNumber, config) {
                if (_.isEmpty(this.logs) || this.logSequence === "none") {
                    return 5;
                }
                let lastLog = this.logs[this.logs.length - 1];
                if (this.logSequence === "log_event_number") {
                    return this.maxLlnLength * config.characterWidth + 8;
                }
                if (this.logSequence === "line_number") {
                    return (this.logs.length - 1 + "").length * config.characterWidth + 5;
                }
                if (this.logSequence === "time_stamp") {
                    return config.characterWidth * 12 + 5;
                }
                if (this.logSequence === "time_passed" || this.logSequence === "event_duration") {
                    let timePassed = DateUtil.getTimeDifferenceStr(this.startTime, lastLog.time);
                    return timePassed.length * config.characterWidth + 5;
                }
            },
            getText: function (session, row) {
                let currentRowLog = this.logs[row];
                if (!currentRowLog || this.logSequence === "none") {
                    return "";
                } else if (this.logSequence === "log_event_number") {
                    let logEventNumber = _.get(currentRowLog, "lln", "");
                    return logEventNumber === -1 ? "" : logEventNumber;
                } else if (this.logSequence === "line_number") {
                    return "#" + (row + 1);
                } else {
                    if (!currentRowLog.time || currentRowLog.isPartOfAnotherLogMsg) {
                        return "";
                    }
                    let isEndTaskLog = currentRowLog.msg.search(/^(\s)*<< /) === 0;
                    let currentLogTimestamp = isEndTaskLog
                        ? currentRowLog.time + currentRowLog.taskDuration
                        : currentRowLog.time;
                    if (this.logSequence === "time_stamp") {
                        let date = new Date(currentLogTimestamp);
                        return date
                            ? (date.getHours() + "").padStart(2, "0") +
                                  ":" +
                                  (date.getMinutes() + "").padStart(2, "0") +
                                  ":" +
                                  (date.getSeconds() + "").padStart(2, "0") +
                                  "." +
                                  (date.getMilliseconds() + "").padStart(3, "0")
                            : "";
                    } else if (this.logSequence === "time_passed") {
                        return DateUtil.getTimeDifferenceStr(this.startTime, currentLogTimestamp);
                    } else if (this.logSequence === "event_duration") {
                        let prevTimeStamp = isEndTaskLog
                            ? currentRowLog.time
                            : _.get(currentRowLog, "previousLogEventTime", undefined);
                        let timePassed = DateUtil.getTimeDifferenceStr(
                            prevTimeStamp,
                            currentLogTimestamp
                        );
                        return timePassed;
                    }
                }
            },
        };
        this._editor.renderer.setShowGutter(false);
        this._editor.renderer.setShowGutter(true);
    }

    _isActionBarVisible() {
        return this._model.buildActionBar && this._actionDiv.style.display !== "none";
    }

    _showLogMessageDetails(e) {
        let currentRow = e.getDocumentPosition().row;
        let currentColumn = e.getDocumentPosition().column;
        let currentLog = this._logsByLineNumber[currentRow];
        if (!this._messageDetailsTooltip) {
            let Tooltip = ace.require("ace/tooltip").Tooltip;
            this._messageDetailsTooltip = new Tooltip(this._rootElement);
        }

        if (
            this._mouseOverLogLevelNotation(currentLog, {row: currentRow, column: currentColumn}) &&
            _.get(currentLog, "fn", undefined)
        ) {
            //show tooltip
            let message = this._buildTooltipMessage(currentLog);
            this._messageDetailsTooltip.setHtml(message);
            this._messageDetailsTooltip.show(null, e.domEvent.pageX - 4, e.domEvent.pageY);
        } else {
            //hide tooltip
            this._messageDetailsTooltip.hide();
        }
    }

    _buildTooltipMessage(currentLog) {
        let message =
            '<div class="arrow-up"></div><div class="log-message-tooltip"><span class="log-event-number">#' +
            currentLog.lln +
            "</span>";
        let date = new Date(currentLog.time);
        message +=
            '<span class="date">' + dateFormat(date, "mmmm dS, yyyy, h:MM:ss TT") + "</span>";
        message += '<span class="level">' + currentLog.lvl + "</span>";
        message +=
            '<span class="message-source">' + currentLog.fn + ":" + currentLog.fln + "</span>";
        message += "</div>";
        return message;
    }

    _isEqualTo(string1, string2, caseSensitive) {
        return caseSensitive
            ? string1 === string2
            : string1.toUpperCase() === string2.toUpperCase();
    }

    _mouseOverLogLevelNotation(currentLog, mousePosition) {
        let logMessage = _.get(currentLog, "msg", "");
        let match = LOG_LEVEL_REGEX.exec(logMessage);
        if (match !== null) {
            let levelNotationStartColumn = _.get(match, "groups.space", "").length;
            let levelNotationEndColumn = levelNotationStartColumn + match.groups.notation.length;
            return (
                mousePosition.column >= levelNotationStartColumn &&
                mousePosition.column <= levelNotationEndColumn
            );
        }
        return false;
    }
    /*******************************************/
    /* END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = LogsControlRenderer;
